import { Controller } from '@hotwired/stimulus'

const STORAGE_KEY = 'eventStagesPanelOpen'
const RECOMPUTE_INTERVAL_MS = 30 * 1000
const DUE_SOON_WINDOW_SECONDS = 30 * 60
// Bubbled custom event from each per-log card. Stimulus's `dispatch('state')`
// inside records/log-event-marker_controller.js produces this name (controller
// identifier + `:` + event name); keep the two in sync if either is renamed.
const CARD_STATE_EVENT = 'records--log-event-marker:state'

export default class extends Controller {
  static targets = ['content', 'chevron', 'totalCount', 'overdueCount', 'dueSoonCount', 'noStageCount']

  connect () {
    const stored = localStorage.getItem(STORAGE_KEY)
    this.isOpen = stored === 'true'
    this.cardStates = new Map()

    this.handleCardState = this.onCardState.bind(this)
    this.element.addEventListener(CARD_STATE_EVENT, this.handleCardState)
    this.recomputeTimer = setInterval(() => this.renderSummary(), RECOMPUTE_INTERVAL_MS)

    this.applyState(false)
    this.renderSummary()
  }

  disconnect () {
    this.element.removeEventListener(CARD_STATE_EVENT, this.handleCardState)
    clearInterval(this.recomputeTimer)
  }

  onCardState (event) {
    const { logId, hasCurrent, nextOccursAt } = event.detail
    this.cardStates.set(logId, { hasCurrent, nextOccursAt })
    this.renderSummary()
  }

  renderSummary () {
    const now = Math.floor(Date.now() / 1000)
    const dueSoonCutoff = now + DUE_SOON_WINDOW_SECONDS

    let overdue = 0
    let dueSoon = 0
    let noStage = 0

    for (const state of this.cardStates.values()) {
      if (!state.hasCurrent) {
        noStage++
      }
      if (state.nextOccursAt !== null) {
        if (state.nextOccursAt < now) {
          overdue++
        } else if (state.nextOccursAt < dueSoonCutoff) {
          dueSoon++
        }
      }
    }

    const total = this.element.querySelectorAll('.event-stages-panel-card').length

    if (this.hasTotalCountTarget) this.totalCountTarget.textContent = total
    if (this.hasOverdueCountTarget) this.overdueCountTarget.textContent = overdue
    if (this.hasDueSoonCountTarget) this.dueSoonCountTarget.textContent = dueSoon
    if (this.hasNoStageCountTarget) this.noStageCountTarget.textContent = noStage
  }

  toggle () {
    this.isOpen = !this.isOpen
    localStorage.setItem(STORAGE_KEY, String(this.isOpen))
    this.applyState(true)
  }

  applyState (animate) {
    const content = this.contentTarget

    this.chevronTarget.classList.toggle('chevron-rotate-up', this.isOpen)
    this.chevronTarget.classList.toggle('chevron-rotate-down', !this.isOpen)

    if (!animate) {
      // Initial state on connect — children may not have laid out yet, so don't
      // pin a measured pixel max-height. Let an open panel auto-size with 'none'.
      content.style.transition = 'none'

      if (this.isOpen) {
        content.style.maxHeight = 'none'
        // Free overflow so Semantic UI dropdowns can extend past the panel edge.
        content.style.overflow = 'visible'
        content.style.opacity = '1'
      } else {
        content.style.maxHeight = '0px'
        // Clear the inline override so the SCSS default (overflow: hidden)
        // clips the collapsed grid.
        content.style.overflow = ''
        content.style.opacity = '0'
      }

      // Force a reflow so the next style change animates from the right starting point.
      void content.offsetHeight
      content.style.transition = ''
      return
    }

    // Animated path — transitions need explicit pixel max-heights at both ends.
    if (this.isOpen) {
      // Clip while animating open so the grid doesn't render below the
      // animating max-height. transitionend below switches to visible once
      // fully open.
      content.style.overflow = 'hidden'
      content.style.maxHeight = `${content.scrollHeight}px`
      content.style.opacity = '1'

      // Once the open animation finishes, unpin the cap and free overflow so
      // later content growth (Mercure updates, dropdown init) isn't clipped
      // and dropdown menus can extend past the panel edge.
      const onEnd = (event) => {
        if (event.propertyName !== 'max-height') return
        content.removeEventListener('transitionend', onEnd)
        if (this.isOpen) {
          content.style.maxHeight = 'none'
          content.style.overflow = 'visible'
        }
      }
      content.addEventListener('transitionend', onEnd)
    } else {
      // Re-clip before the close animation so collapsing content gets hidden
      // (we may have been at overflow: visible while fully open).
      content.style.overflow = 'hidden'
      // Convert any 'none' max-height back to a pixel value so the transition can run.
      content.style.maxHeight = `${content.scrollHeight}px`
      void content.offsetHeight
      content.style.maxHeight = '0px'
      content.style.opacity = '0'
    }
  }
}
